import React, { createContext, useState, useEffect } from 'react'
import { AppConfigDataClient, StartConfigurationSessionCommand, GetLatestConfigurationCommand } from '@aws-sdk/client-appconfigdata'
import { fromCognitoIdentityPool } from '@aws-sdk/credential-providers'
import { useAuth } from '../auth/useAuth.js'

const DEFAULTS = {}

export const FlagsContext = createContext(DEFAULTS)

const IDENTITY_POOL_ID = import.meta.env.VITE_COGNITO_IDENTITY_POOL_ID || ''
const APP_ID = import.meta.env.VITE_APPCONFIG_APP_ID || ''
const ENV_ID = import.meta.env.VITE_APPCONFIG_ENV_ID || ''
// Two profiles now back the AppConfig layer — dev-flags (developer rollouts)
// and org-features (super-admin toggles). We fetch both and merge their
// `values` maps so call sites keep seeing a flat `flags.<key>.enabled` shape.
const DEV_FLAGS_PROFILE_ID = import.meta.env.VITE_APPCONFIG_DEV_FLAGS_PROFILE_ID || ''
const ORG_FEATURES_PROFILE_ID = import.meta.env.VITE_APPCONFIG_ORG_FEATURES_PROFILE_ID || ''
const REGION = import.meta.env.VITE_COGNITO_REGION || 'eu-west-1'
const USER_POOL_ID = import.meta.env.VITE_COGNITO_USER_POOL_ID || ''

export function FlagsProvider({ children }) {
  const { user } = useAuth()
  const [flags, setFlags] = useState(DEFAULTS)

  useEffect(() => {
    if (!user?.idToken || !IDENTITY_POOL_ID || !APP_ID || !ENV_ID) return
    if (!DEV_FLAGS_PROFILE_ID && !ORG_FEATURES_PROFILE_ID) return

    let cancelled = false

    async function fetchProfile(client, profileId) {
      if (!profileId) return {}
      const session = await client.send(new StartConfigurationSessionCommand({
        ApplicationIdentifier: APP_ID,
        EnvironmentIdentifier: ENV_ID,
        ConfigurationProfileIdentifier: profileId,
      }))
      const config = await client.send(new GetLatestConfigurationCommand({
        ConfigurationToken: session.InitialConfigurationToken,
      }))
      const body = new TextDecoder().decode(config.Configuration)
      if (!body) return {}
      // `appconfigdata.GetLatestConfiguration` on a feature-flag profile
      // returns a flat map of resolved flags (`{ flagKey: { enabled, ... } }`).
      // This is distinct from the admin `GetHostedConfigurationVersion` API,
      // which returns the authored `{flags, values, defaults}` shape — don't
      // confuse the two.
      return JSON.parse(body) || {}
    }

    async function fetchAll() {
      try {
        const providerName = `cognito-idp.${REGION}.amazonaws.com/${USER_POOL_ID}`
        const client = new AppConfigDataClient({
          region: REGION,
          credentials: fromCognitoIdentityPool({
            identityPoolId: IDENTITY_POOL_ID,
            logins: { [providerName]: user.idToken },
            clientConfig: { region: REGION },
          }),
        })

        const [devValues, orgValues] = await Promise.all([
          fetchProfile(client, DEV_FLAGS_PROFILE_ID),
          fetchProfile(client, ORG_FEATURES_PROFILE_ID),
        ])
        if (cancelled) return
        // Flag keys should be unique across profiles; if they ever collide
        // org-features wins (user-facing toggle is typically the authoritative one).
        setFlags({ ...devValues, ...orgValues })
      } catch (err) {
        if (import.meta.env.MODE !== 'test') {
          console.warn('Failed to fetch feature flags:', err.message)
        }
      }
    }

    fetchAll()
    return () => { cancelled = true }
  }, [user?.idToken])

  return (
    <FlagsContext.Provider value={flags}>
      {children}
    </FlagsContext.Provider>
  )
}
